/**
 * État et destination d'une carte du planning v2 (docs/ux-v2/SPEC.md PL3, PL4, PL11, PL13, § 3.6). Fonctions
 * pures : la fenêtre de réservation (utils/functions.ts isClassBookable) est passée en paramètre.
 *
 * Règles reprises de screens/Classes/components/Card.tsx :
 * - cliquable si réservable dans la fenêtre du site (`days`), ou si c'est une « course » (enrollment) à venir ;
 * - bouton Réserver si des places restent (MaxCapacity − TotalBooked), sinon Liste d'attente, ou Complet si la liste
 *   d'attente est pleine (TotalBookedWaitlist ≥ WaitListSize) ;
 * - mode coach : nouvel onglet sur la page de réservation ;
 * - Run Club (programme 46) des sites RIISE et Punch Paris : planning Dynamo, studio 10 (entrée « Run Club » `10-46` si
 *   le studio Monday Event est séparé, lib/planning/eventStudios.ts), semaine du cours.
 * Ajouts v2 : « Réservé » (visite du client), « En cours » puis « Terminé » neutres, « Ouvre le … » hors fenêtre, et cours d'une autre
 * marque de la ville (retour de Luc du 04/10, SPEC § 1 règle 4, PL11) : mêmes états que les cours du site (Réserver,
 * Liste d'attente, Complet, Ouvre le…), mais la fiche s'ouvre dans un nouvel onglet SUR LE SITE DE LA MARQUE, où se
 * fait la réservation (jamais de réservation croisée) ; sans adresse connue pour la marque, carte inerte « Sur le site
 * … ». La fenêtre de réservation appliquée est celle du site courant (`days` : 15 jours sur les 10 sites le 04/10).
 * Aucun nombre de places n'est exposé (P8).
 */
import { format, getISOWeek } from 'date-fns';
import type { PlanningClass } from '../../api/planning';
import type { PlanningBrand } from './brands';
import { bookingOpensAt } from './week';

/** `nolink` : cours d'une autre marque dont la page de réservation n'est pas connue (aucun lien possible). */
export type ClassState = 'book' | 'waitlist' | 'full' | 'mine' | 'ongoing' | 'past' | 'soon' | 'nolink';

/** Couleur de l'étiquette d'état. */
export type StateTone = 'action' | 'amber' | 'neutral' | 'green' | 'muted';

export interface ClassStatus {
  state: ClassState;
  label: string;
  tone: StateTone;
}

export interface StatusContext {
  now: Date;
  /** Le cours est-il sur le site qui affiche le planning ? */
  isCurrentSite: boolean;
  /** = utils/functions.ts isClassBookable (fenêtre `days` du site, cours futur). */
  isBookable: (c: PlanningClass) => boolean;
  /** Le client connecté a une visite (non annulée) sur ce cours. */
  isMine: boolean;
  brand: Pick<PlanningBrand, 'name' | 'url'>;
  /** Réglage `days` du site courant. */
  numberOfDays: number;
}

/** = Card.tsx : Run Club (programme 46) des sites RIISE (5736555) et Punch (5736551) Paris. */
export const RUN_CLUB_PROGRAM_ID = 46;
export const RUN_CLUB_SITE_IDS: ReadonlyArray<string> = ['5736555', '5736551'];
/** = Card.tsx : la réservation du Run Club se fait sur Dynamo Paris, studio 10. */
export const RUN_CLUB_HOME_SITE_ID = '5736526';
export const RUN_CLUB_STUDIO_ID = 10;
export const RUN_CLUB_FALLBACK_URL = 'https://dynamo-cycling.com/reservation/';

/** Run Club d'un site RIISE / Punch Paris : il se réserve sur Dynamo Paris, jamais sur son propre site. */
export const isRunClubCopy = (c: PlanningClass) =>
  c.ClassDescription?.Program?.Id === RUN_CLUB_PROGRAM_ID && RUN_CLUB_SITE_IDS.includes(String(c.SiteId));

export const isEnrollment = (c: PlanningClass) => c.ClassDescription?.Program?.ScheduleType === 'Enrollment';

export const hasCapacity = (c: PlanningClass) => (c.MaxCapacity ?? 0) - (c.TotalBooked ?? 0) > 0;

export const isWaitlistFull = (c: PlanningClass) => (c.TotalBookedWaitlist ?? 0) >= (c.WaitListSize ?? 0);

const capacityStatus = (c: PlanningClass): ClassStatus =>
  hasCapacity(c)
    ? { state: 'book', label: 'Réserver', tone: 'action' }
    : isWaitlistFull(c)
      ? { state: 'full', label: 'Complet', tone: 'neutral' }
      : { state: 'waitlist', label: 'Liste d’attente', tone: 'amber' };

/**
 * Fin du cours : EndDateTime, ou le début si elle manque ou précède le début (donnée Mindbody incomplète : le cours
 * passe alors à « Terminé » dès son début, comme avant).
 */
function classEnd(c: PlanningClass, start: Date): Date {
  const end = c.EndDateTime ? new Date(c.EndDateTime) : null;
  return end && !Number.isNaN(end.getTime()) && end.getTime() > start.getTime() ? end : start;
}

export function classStatus(c: PlanningClass, ctx: StatusContext): ClassStatus {
  const start = new Date(c.StartDateTime);
  const nowTime = ctx.now.getTime();
  // « Terminé » seulement après la fin de la séance ; entre le début et la fin, le cours est « En cours » (revue du
  // 05/10 : un cours 12:30–13:15 réservé passait à « Terminé » à 12:30). Le cours du client garde « Réservé ».
  if (classEnd(c, start).getTime() <= nowTime) return { state: 'past', label: 'Terminé', tone: 'muted' };
  if (start.getTime() <= nowTime) {
    return ctx.isMine
      ? { state: 'mine', label: 'Réservé', tone: 'green' }
      : { state: 'ongoing', label: 'En cours', tone: 'neutral' };
  }
  if (ctx.isMine) return { state: 'mine', label: 'Réservé', tone: 'green' };
  // Autre marque sans page de réservation connue : on ne peut pas y envoyer le visiteur (bouton désactivé, expliqué).
  // Le Run Club se réserve sur Dynamo Paris, dont l'adresse est toujours connue (classTarget).
  if (!ctx.isCurrentSite && !ctx.brand.url && !isRunClubCopy(c)) {
    return { state: 'nolink', label: `Sur le site ${ctx.brand.name}`.trim(), tone: 'neutral' };
  }
  if (ctx.isBookable(c) || isEnrollment(c)) return capacityStatus(c);
  const opens = bookingOpensAt(start, ctx.numberOfDays);
  return opens.getTime() > ctx.now.getTime()
    ? { state: 'soon', label: `Ouvre le ${format(opens, 'dd/MM')}`, tone: 'neutral' }
    : { state: 'soon', label: 'Bientôt', tone: 'neutral' };
}

/* ------------------------------------------------------------------- destination */

export type ClassTarget =
  | { kind: 'route'; to: string }
  /** Nouvel onglet sur CE site, par window.open (mode coach ; le shim du thème réécrit /reservation/). */
  | { kind: 'open'; href: string }
  /**
   * Nouvel onglet sur le site d'une autre marque (openCrossSite, lib/crossSite.ts) : cours d'une autre marque, ou
   * Run Club des sites RIISE / Punch Paris (réservé sur Dynamo). `brandName` : marque du site ouvert ('' si inconnue).
   */
  | { kind: 'external'; href: string; siteId: string; brandName: string }
  | { kind: 'none' };

export interface TargetContext {
  status: ClassStatus;
  currentSiteId: string;
  /** Shortcode `[mindbody coach]`. */
  coachMode: boolean;
  /** Page de réservation du site courant (mode coach) ; '' = « /reservation/ » comme la v1. */
  reservationUrl: string;
  /** Marque du cours. */
  brand: Pick<PlanningBrand, 'url' | 'name'>;
  /** Page de réservation de Dynamo Paris si connue (Run Club). */
  runClubUrl: string | null;
  /** Nom de la marque Dynamo Paris si connu (Run Club, nom accessible du lien). */
  runClubName?: string;
  /**
   * Studio du Run Club dans la route du planning Dynamo (lib/planning/eventStudios.ts runClubStudioRouteId) : « 10-46 »
   * (entrée « Run Club ») si la séparation du studio Monday Event est réglée sur le site qui affiche, sinon « 10 ».
   */
  runClubStudioId?: string;
}

/** `https://…/reservation/` + `#/class/123` (le fragment d'origine est retiré). */
export function hashUrl(base: string, route: string): string {
  return `${base.replace(/#.*$/, '')}#${route}`;
}

export function classRoute(c: PlanningClass): string {
  return isEnrollment(c) ? `/class/${c.Id}/enrollment` : `/class/${c.Id}`;
}

export function classTarget(c: PlanningClass, ctx: TargetContext): ClassTarget {
  const { state } = ctx.status;
  if (state === 'past' || state === 'ongoing' || state === 'soon' || state === 'nolink') return { kind: 'none' };

  const site = String(c.SiteId);
  if (isRunClubCopy(c)) {
    const studio = ctx.runClubStudioId ?? String(RUN_CLUB_STUDIO_ID);
    const route = `/studio/${studio}/week/${getISOWeek(new Date(c.StartDateTime))}`;
    const href = hashUrl(ctx.runClubUrl ?? RUN_CLUB_FALLBACK_URL, route);
    if (ctx.currentSiteId === RUN_CLUB_HOME_SITE_ID)
      return ctx.coachMode ? { kind: 'open', href } : { kind: 'route', to: route };
    return { kind: 'external', href, siteId: RUN_CLUB_HOME_SITE_ID, brandName: ctx.runClubName ?? '' };
  }

  if (site !== ctx.currentSiteId) {
    return ctx.brand.url
      ? { kind: 'external', href: hashUrl(ctx.brand.url, classRoute(c)), siteId: site, brandName: ctx.brand.name }
      : { kind: 'none' };
  }

  if (ctx.coachMode) return { kind: 'open', href: hashUrl(ctx.reservationUrl || '/reservation/', classRoute(c)) };
  return { kind: 'route', to: classRoute(c) };
}
